import { RootState } from '@propertypal/shared/src/reducers/store';
import { configureGtag, trackPageView } from '@propertypal/shared/src/services/analytics';
import defaultTheme from '@propertypal/shared/src/styles/theme/default';
import { Property } from '@propertypal/shared/src/types/property';
import setEmailHashes from '@propertypal/shared/src/utils/liveramp';
import PageProgress from '@propertypal/web-ui/src/animation/PageProgress';
import SkipToContent from '@propertypal/web-ui/src/buttons/SkipToContent';
import Header from '@propertypal/web-ui/src/navigation/Header';
import MobileFooter from '@propertypal/web-ui/src/navigation/MobileFooter';
import Tooltip from '@propertypal/web-ui/src/notifications/Tooltip';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import Script from 'next/script';
import React, { FunctionComponent, ReactNode, useEffect } from 'react';
import { ThemeProvider } from 'styled-components';
import config from '../../../../../app.config.json';
import prebid from '../../../public/prebid.lib.js?src';
import quantcast from '../../../public/quantcast.lib.js?src';
import { onConsent } from '../../services/ads/consentListener';
import Footer from '../footer/Footer';

interface ListItem {
  url: string;
  label: string;
}

interface SiteMapData {
  parent: ListItem;
  children: ListItem[];
}

interface Props {
  siteMapLinks: SiteMapData[] | null;
  cfIpCountry: string | null;
  disableLayout?: boolean;
  children: ReactNode;
  property?: Property;
  initialState: RootState;
  emailHashes?: string[];
}

const ToastProvider = dynamic(() => import('@propertypal/web-ui/src/toast/ToastContext'), { ssr: false });

const Layout: FunctionComponent<Props> = (props) => {
  const router = useRouter();

  // Triggers the CMP on every page
  useEffect(() => {
    onConsent(
      () => {
        configureGtag({
          logged_in: props.initialState?.user?.loggedIn,
          property_id: props.property?.id,
          account_number: props.property?.account?.accountNumber,
        });
        trackPageView();
      },
      [1],
      [],
    );
  }, [router.asPath]);

  return (
    <ThemeProvider theme={defaultTheme}>
      {!props.disableLayout && (
        <div style={{ minHeight: '100vh' }}>
          <SkipToContent />

          <Header cfIpCountry={props.cfIpCountry} />
          <div id="main" tabIndex={-1}>
            {props.children}
          </div>
          <Footer siteMapData={props.siteMapLinks} cfIpCountry={props.cfIpCountry} />
          <MobileFooter cfIpCountry={props.cfIpCountry} />
        </div>
      )}

      {props.disableLayout && (
        <div id="main" tabIndex={-1}>
          {props.children}
        </div>
      )}

      <div id="modal-root" />

      <PageProgress />
      <Tooltip />
      <ToastProvider />

      <script src={`https://www.googletagmanager.com/gtag/js?id=${config.googleAnalyticsKey}`} async />
      <Script strategy="beforeInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
        `}
      </Script>
      <script src={quantcast} />
      <script src="https://securepubads.g.doubleclick.net/tag/js/gpt.js" async />
      <script src={prebid} async />
      <script src={`https://www.google.com/recaptcha/enterprise.js?render=${config.recaptchaKey}`} async defer />
      <Script
        src="https://launchpad-wrapper.privacymanager.io/ebb31f85-ef22-4d70-97ff-9a9c1d694324/launchpad-liveramp.js"
        onLoad={() => {
          setEmailHashes(props.emailHashes);
        }}
      />
    </ThemeProvider>
  );
};

export default Layout;
